import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { RiskFeature, RiskKind, RiskModelExplainer } from '#services/api/mia.ts';

import * as React from 'react';
import {
  faCalendarXmark,
  faChartSimple,
  faCheck,
  faCircleInfo,
  faClockRotateLeft,
  faShieldCheck,
  faSliders,
} from '@fortawesome/pro-regular-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';

import { attendanceRiskCopy, productName } from './Readiness.utils.ts';

/**
 * The slider spans nearly the whole probability range rather than a narrower "sensible" band.
 *
 * A band in probability can't be sensible for every practice, because it is implicitly a claim about
 * a distribution: 60% is "only the riskiest" for an org whose predictions cluster low, and "everyone"
 * for one clustered high. The number staff can actually reason about — how many patients they will
 * see — is the live flagged count beside the slider, which is true per-org in a way no fixed endpoint
 * is. So the band stays out of the way and the count does the guiding.
 *
 * It stops short of 0 and 100 because neither is a threshold: everything is at or above 0, and a
 * probability of exactly 1 is not something the model emits.
 */
const thresholdSliderMin = 1;
const thresholdSliderMax = 99;
const thresholdSliderStep = 1;

// Per-strongest-predictor icons, keyed by the model feature. Unknown → a neutral chart glyph.
const featureIcons: Record<string, IconDefinition> = {
  historical_no_show_rate: faClockRotateLeft,
  historical_late_cancel_rate: faCalendarXmark,
};

/**
 * The `referenceGroup` of a feature's comparison, phrased for the "patients with ___" clause — the
 * riskier side, since the copy is framed negatively. A feature absent from here has no phrasing we
 * can state correctly, so the card falls back to the feature's own description rather than describing
 * the wrong group; that's also why this is keyed by feature rather than by risk kind, so a future
 * comparison split on something else (confirmed, form completed) brings its own phrase.
 */
const featureReferenceGroups: Record<string, string> = {
  historical_no_show_rate: 'prior no-shows',
  historical_late_cancel_rate: 'prior late cancellations',
};

/**
 * The outcome each model predicts, phrased for the "more likely to ___" clause. A comparison contrasts
 * the two groups' rates of that outcome, and which outcome it is depends on the org's model — so this
 * is keyed by risk kind, not by the feature. "Again" is accurate because the reference group is
 * defined by having already done it.
 */
const riskKindOutcomes: Record<RiskKind, string> = {
  no_show: 'no-show again',
  late_cancel: 'cancel late again',
};

// The slider works in whole percent while the threshold is a 0..1 probability, so the two cross this
// boundary in both directions. Rounding makes the trip lossy: `fromPercent(toPercent(x))` is `x` only
// for an `x` the slider could have produced, which is why the draft is compared in percent rather
// than round-tripped back and compared as a probability.
function toPercent(threshold: number): number {
  return Math.round(threshold * 100);
}

function fromPercent(percent: number): number {
  return percent / 100;
}

function formatScoreShare(scoreShare: number): string {
  return `${Math.round(scoreShare * 100)}% of the score`;
}

// At or above a doubling the relative risk reads naturally as a multiplier; below one it reads better
// as the increase it represents ("15% more likely" over "1.2× more likely").
const multiplierFloor = 2;

/**
 * The magnitude token for a relative risk, unit included — `"3×"`, `"3.4×"`, or `"15%"`.
 *
 * Rounding to whole percent is safe only because the server withholds any comparison below a 5%
 * relative risk: the smallest value that reaches here is 1.05, which renders "5%". Were that floor
 * ever lowered past 1.01, this would round a real difference to "0% more likely" — copy that denies
 * the very thing the wire asserted.
 *
 * Multipliers round to a tenth, and a ratio that rounds to a whole number loses its `.0` — so 2 and
 * 2.02 both read "2×" rather than disagreeing over a difference too small to have rendered.
 */
function formatRiskMagnitude(riskRatio: number): string {
  if (riskRatio >= multiplierFloor) {
    const roundedRatio = Math.round(riskRatio * 10) / 10;
    return `${Number.isInteger(roundedRatio) ? roundedRatio : roundedRatio.toFixed(1)}×`;
  }

  return `${Math.round((riskRatio - 1) * 100)}%`;
}

/**
 * The explainer read, as the modal consumes it. A union rather than a value plus loading/error flags
 * because the three cases render three different things and `failed` must never be able to
 * masquerade as `ready` with no data — which is what would report an outage as a fact about the org.
 */
export type ExplainerState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; explainer: RiskModelExplainer };

/**
 * How the resolved threshold was arrived at, which decides what the modal may claim about it.
 * `recommended` is Mend's per-org calculation and is labelled as such; `derived` is the client's own
 * estimate from today's loaded rows and must not borrow that label; `fallback` is a global constant
 * and claims nothing. `saved` is the admin's own persisted number, and `draft` the one they are
 * moving right now — distinct from `saved` because an unsaved value is not saved, and saying so
 * through the label table would only hold while `saved` happens to be unlabelled.
 */
export type ThresholdBasis = 'saved' | 'recommended' | 'derived' | 'fallback' | 'draft';

/**
 * What the chip beside the threshold says, per basis. Undefined means no chip: an admin's saved
 * number needs no explaining, and the global fallback has nothing to say for itself. Only
 * `recommended` may say "Recommended" — the derived estimate is read off one day's worth of rows and
 * would be overclaiming with that word.
 */
const thresholdBasisLabels: Record<ThresholdBasis, string | undefined> = {
  saved: undefined,
  fallback: undefined,
  draft: undefined,
  recommended: 'Recommended',
  derived: 'Your riskiest today',
};

/**
 * The org's threshold read. `basis` rides on the `ready` case because it only means anything next to
 * a resolved value.
 */
export type ThresholdState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; threshold: number; basis: ThresholdBasis };

interface RiskModelDialogProps {
  onClose: () => void;
  orgName: string;
  explainerState: ExplainerState;
  thresholdState: ThresholdState;
  /** Non-null predicted risk of every loaded row, for the live flagged count. */
  riskProbabilities: number[];
  canEdit: boolean;
  isSaving: boolean;
  onSaveThreshold: (threshold: number) => void;
}

/**
 * "How Coordinate predicts attendance risk" modal (2026-07-29 comp). A Mend-prediction-engine
 * marketing callout, the single strongest predictor personalized to the org (from the explainer's
 * `features[0]`: label + score-share chip + optional descriptive comparison line), and the org's
 * risk threshold. The flagged count recomputes client-side from the already-loaded worklist
 * probabilities — no per-drag server call. Admins can move + save the threshold; other staff see it
 * read-only. The full feature grid is a deferred UI.
 */
export function RiskModelDialog(props: RiskModelDialogProps) {
  const titleId = React.useId();

  return (
    <Dialog.Root open onClose={props.onClose} maxWidth="lg" fullWidth aria-labelledby={titleId}>
      <Dialog.Header>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1,
              flexShrink: 0,
              bgcolor: 'action.selected',
              color: 'quaternary.main',
            }}
          >
            <FontAwesomeScalableIcon icon={faSliders} fontSize="small" color="inherit" />
          </Box>
          {/* Takes the row's remaining width. Left to size itself this flex item shrinks to its
              longest child, which left the title laying out in a fraction of the header and wrapping
              mid-phrase. */}
          <Box flex={1} minWidth={0}>
            <Dialog.Title id={titleId}>{attendanceRiskCopy.modalTitle}</Dialog.Title>
            <Typography component="p" variant="body2" color="text.secondary">
              Powered by Mend&apos;s prediction engine, tuned to your practice.
            </Typography>
          </Box>
        </Stack>
      </Dialog.Header>

      {/* Mounting the editable body only once the threshold is `ready` is what lets it seed its
          draft from a real value: the config landing while the modal is open mounts it fresh rather
          than leaving a draft that was seeded from nothing. */}
      {props.thresholdState.status === 'ready' ? (
        <ThresholdDraftBody
          threshold={props.thresholdState.threshold}
          basis={props.thresholdState.basis}
          explainerState={props.explainerState}
          orgName={props.orgName}
          riskProbabilities={props.riskProbabilities}
          canEdit={props.canEdit}
          isSaving={props.isSaving}
          onSaveThreshold={props.onSaveThreshold}
        />
      ) : (
        <DialogBody
          explainerState={props.explainerState}
          orgName={props.orgName}
          thresholdSection={<UnresolvedThreshold status={props.thresholdState.status} />}
        />
      )}
    </Dialog.Root>
  );
}

/**
 * Everything the modal renders regardless of the threshold's state: the engine callout, the
 * strongest predictor, whatever the threshold section currently is, and the footer. `saveAction` is
 * omitted whenever there is nothing to save.
 */
function DialogBody(props: {
  explainerState: ExplainerState;
  orgName: string;
  thresholdSection: React.ReactNode;
  saveAction?: React.ReactNode;
}) {
  return (
    <>
      <Dialog.Content>
        <Stack direction="column" spacing={3}>
          <PredictionEngineCallout />

          <StrongestPredictor explainerState={props.explainerState} orgName={props.orgName} />

          {props.thresholdSection}
        </Stack>
      </Dialog.Content>

      <Dialog.Footer>
        <Dialog.DismissButton>Cancel</Dialog.DismissButton>
        {props.saveAction}
      </Dialog.Footer>
    </>
  );
}

/** The threshold section with no threshold to show — still loading, or the read failed. */
function UnresolvedThreshold(props: { status: 'loading' | 'failed' }) {
  return (
    <Box>
      <SectionHeading>Your risk threshold</SectionHeading>
      {props.status === 'loading' ? (
        <Skeleton variant="rounded" height={72} sx={{ mt: 1 }} />
      ) : (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
          Your risk threshold could not be loaded. Please try again later.
        </Alert>
      )}
    </Box>
  );
}

/**
 * {@link RiskModelDialogProps} minus the close handler the body never touches, with the threshold
 * union resolved to its ready values — the call site's guard did the narrowing, so the body asks
 * for plain scalars instead of re-proving it.
 */
interface ThresholdDraftBodyProps extends Omit<RiskModelDialogProps, 'onClose' | 'thresholdState'> {
  threshold: number;
  basis: ThresholdBasis;
}

/** The modal with a resolved threshold: owns the admin's unsaved draft of it. */
function ThresholdDraftBody(props: ThresholdDraftBodyProps) {
  const activePercent = toPercent(props.threshold);
  const [draftPercent, setDraftPercent] = React.useState(activePercent);
  // The config keeps polling while the modal is open, so the active threshold can change under a
  // mounted body (another admin saved). Re-seed the draft — but only an untouched one: seedPercent
  // remembers what the draft was seeded from, which is what makes "untouched" knowable. A dirty
  // draft is the admin's in-progress choice and keeps last-writer-wins semantics on save; without
  // this, an untouched slider would go spuriously dirty and Save would submit the superseded value.
  const [seedPercent, setSeedPercent] = React.useState(activePercent);
  if (seedPercent !== activePercent) {
    setSeedPercent(activePercent);
    if (draftPercent === seedPercent) {
      setDraftPercent(activePercent);
    }
  }

  const draftThreshold = fromPercent(draftPercent);
  const isDirty = draftPercent !== activePercent;
  // The percent round trip is lossy, so an untouched draft counts against the resolved threshold
  // itself — otherwise this count disagrees with the briefing's for any threshold that isn't a
  // whole percent (a derived or recommended one usually isn't).
  const flaggedCount = props.riskProbabilities.filter(
    (probability) => probability >= (isDirty ? draftThreshold : props.threshold)
  ).length;

  function handleSliderChange(_event: Event, value: number | number[]) {
    setDraftPercent(Array.isArray(value) ? (value[0] ?? draftPercent) : value);
  }

  return (
    <DialogBody
      explainerState={props.explainerState}
      orgName={props.orgName}
      thresholdSection={
        <ThresholdControl
          draftPercent={draftPercent}
          basis={isDirty ? 'draft' : props.basis}
          flaggedCount={flaggedCount}
          canEdit={props.canEdit}
          onSliderChange={handleSliderChange}
        />
      }
      saveAction={
        props.canEdit ? (
          <Button
            variant="contained"
            color="quaternary"
            size="large"
            startIcon={<FontAwesomeScalableIcon icon={faCheck} />}
            disabled={!isDirty || props.isSaving}
            onClick={() => props.onSaveThreshold(draftThreshold)}
          >
            Save changes
          </Button>
        ) : null
      }
    />
  );
}

function SectionHeading(props: { children: React.ReactNode }) {
  return (
    <Typography component="p" variant="subtitle1" fontWeight={600}>
      {props.children}
    </Typography>
  );
}

// Marketing claims, not measurements — they change on marketing's cadence.
const enginePredictionFigures = {
  appointments: '40 million appointments',
  patientInteractions: '200 million patient interactions',
} as const;

function PredictionEngineCallout() {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2.5,
        // Subtle top-to-bottom sheen over the dialog surface, matching the comp's callout.
        background: (theme) =>
          `linear-gradient(160deg, ${alpha(theme.palette.common.white, 0.05)} 0%, ${alpha(
            theme.palette.common.white,
            0.015
          )} 100%)`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <FontAwesomeScalableIcon
          icon={faShieldCheck}
          fontSize="small"
          sx={{ color: 'quaternary.main' }}
        />
        <Typography
          component="p"
          variant="caption"
          color="quaternary.main"
          fontWeight={700}
          textTransform="uppercase"
        >
          Mend prediction engine
        </Typography>
      </Stack>
      <Typography component="p" variant="body1" color="text.secondary">
        Powered by over a decade of data comprised of{' '}
        <Box component="span" fontWeight={700} color="text.primary">
          {enginePredictionFigures.appointments}
        </Box>{' '}
        and{' '}
        <Box component="span" fontWeight={700} color="text.primary">
          {enginePredictionFigures.patientInteractions}
        </Box>
        , {productName} combines AI with your organization&apos;s unique patient engagement history
        to identify who needs attention next — and what action is most likely to keep them on track
        for care.
      </Typography>
    </Box>
  );
}

function StrongestPredictor(props: { explainerState: ExplainerState; orgName: string }) {
  const { explainerState } = props;
  const strongest =
    explainerState.status === 'ready' ? explainerState.explainer.features[0] : undefined;

  return (
    <Box>
      <SectionHeading>The strongest predictor at {props.orgName}</SectionHeading>

      {explainerState.status === 'loading' ? (
        <Skeleton variant="rounded" height={88} sx={{ mt: 1 }} />
      ) : explainerState.status === 'failed' ? (
        // An outage is ours, not a fact about their practice — saying "not available for you yet"
        // here would report a failed fetch as a property of the org.
        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
          {productName}&apos;s strongest predictor could not be loaded. Please try again later.
        </Alert>
      ) : !strongest ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          {productName}&apos;s strongest predictor isn&apos;t available for this practice yet.
        </Alert>
      ) : (
        <StrongestPredictorCard
          feature={strongest}
          riskKind={explainerState.explainer.riskKind}
          orgName={props.orgName}
        />
      )}
    </Box>
  );
}

function StrongestPredictorCard(props: {
  feature: RiskFeature;
  riskKind: RiskKind;
  orgName: string;
}) {
  const { feature } = props;
  const referenceGroup = featureReferenceGroups[feature.key];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2,
        mt: 1,
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: 'action.hover',
          color: 'quaternary.main',
        }}
      >
        <FontAwesomeScalableIcon
          icon={featureIcons[feature.key] ?? faChartSimple}
          fontSize="small"
          color="inherit"
        />
      </Box>

      <Box flex={1} minWidth={0}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography component="p" variant="subtitle2">
            {feature.label} at {props.orgName}
          </Typography>
          <Chip label={formatScoreShare(feature.scoreShare)} color="quaternary" size="small" />
        </Stack>

        <Typography component="p" variant="body2" color="text.secondary" mt={0.5}>
          {referenceGroup === undefined || !feature.comparison ? (
            feature.description
          ) : (
            <>
              At your practice, patients with {referenceGroup} are{' '}
              <Box component="span" fontWeight={700} color="text.primary">
                {formatRiskMagnitude(feature.comparison.riskRatio)} more likely
              </Box>{' '}
              to {riskKindOutcomes[props.riskKind]}.
            </>
          )}
        </Typography>
      </Box>
    </Stack>
  );
}

function ThresholdControl(props: {
  draftPercent: number;
  basis: ThresholdBasis;
  flaggedCount: number;
  canEdit: boolean;
  onSliderChange: (event: Event, value: number | number[]) => void;
}) {
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}
        flexWrap="wrap"
      >
        <SectionHeading>Your risk threshold</SectionHeading>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography component="span" variant="body2" color="text.secondary">
            {props.flaggedCount} {props.flaggedCount === 1 ? 'appointment' : 'appointments'} would
            be flagged today
          </Typography>
          {thresholdBasisLabels[props.basis] === undefined ? null : (
            <Chip label={thresholdBasisLabels[props.basis]} size="small" variant="outlined" />
          )}
          {/* Neutral rather than accented, matching the comp and the slider it labels. */}
          <Typography component="span" variant="h5" color="text.primary">
            {props.draftPercent}%
          </Typography>
        </Stack>
      </Stack>

      <Typography component="p" variant="body2" color="text.secondary" mt={0.5}>
        Flag a patient in the daily briefing when {productName} predicts their{' '}
        {attendanceRiskCopy.riskNoun} is at or above this level. Lower it to catch more patients;
        raise it to focus only on the riskiest.
      </Typography>

      <Box px={1} pt={2}>
        <Slider
          value={props.draftPercent}
          onChange={props.onSliderChange}
          min={thresholdSliderMin}
          max={thresholdSliderMax}
          step={thresholdSliderStep}
          disabled={!props.canEdit}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${value}%`}
          aria-label="Your risk threshold"
          sx={{ color: 'text.primary' }}
        />
        <Stack direction="row" justifyContent="space-between">
          <Typography component="span" variant="caption" color="text.secondary">
            {thresholdSliderMin}% · more patients flagged
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            {thresholdSliderMax}% · only the riskiest
          </Typography>
        </Stack>
      </Box>

      {!props.canEdit ? (
        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
          <FontAwesomeScalableIcon
            icon={faCircleInfo}
            fontSize="small"
            sx={{ color: 'text.secondary' }}
          />
          <Typography component="span" variant="caption" color="text.secondary">
            Only practice admins can change this threshold.
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}
